import { useEffect, useRef } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import useAuth from "./useAuth";
import { useSubscriptionQuery } from "./subscription/useSubscriptionQuery";
import { identifyUser } from "@/utils/gtm";

/**
 * Hex-encodes the SHA-256 of `value` using SubtleCrypto. Returns
 * `undefined` when SubtleCrypto isn't available (legacy browsers,
 * non-HTTPS dev origins) so callers can omit the field cleanly.
 */
async function sha256Hex(value: string): Promise<string | undefined> {
  try {
    if (!globalThis.crypto?.subtle) return undefined;
    const buffer = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
}

/**
 * Identifies the authenticated user to GTM/GA4 once per session.
 *
 * - For chargebee users: waits for subscription data before firing so that
 *   subscription_status, plan, age, and trial fields are included.
 * - For all other users (corporate/direct/internal): fires immediately.
 * - Hashes the user's email (SHA-256) for enhanced conversion matching
 *   downstream in sGTM. Raw email is never pushed to the dataLayer.
 */
export const useUserIdentification = () => {
  const { authenticated, keycloakInstance, tokenParsed } = useAuth();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionQuery();
  const hasFired = useRef(false);

  useEffect(() => {
    if (!authenticated || !tokenParsed || hasFired.current) return;

    const tp = tokenParsed as KeycloakTokenParsed & {
      access_type?: string;
      email?: string;
    };
    const accessType = tp.access_type ?? "unknown";

    // Chargebee users: wait for subscription data before firing
    if (accessType === "chargebee" && isSubscriptionLoading) return;

    hasFired.current = true;

    const appRoles = tp.resource_access?.app?.roles ?? [];
    const clientId = keycloakInstance?.clientId;
    const clientRoles = clientId
      ? tp.resource_access?.[clientId]?.roles ?? []
      : [];

    const email = tp.email;
    const isInternal =
      (email?.endsWith("@newsquawk.com") && !email?.includes("+external")) ||
      clientRoles.includes("internal-newsquawk-user");

    const userType = isInternal ? "internal" : accessType;

    const subscriptionAgeDays = subscription?.created_at
      ? Math.floor(
          (Date.now() - new Date(subscription.created_at).getTime()) / 86400000
        )
      : null;

    const trialDaysRemaining = subscription?.trial_end
      ? Math.max(
          0,
          Math.floor(
            (new Date(subscription.trial_end).getTime() - Date.now()) / 86400000
          )
        )
      : null;

    (async () => {
      const emailSha256 = email
        ? await sha256Hex(email.trim().toLowerCase())
        : undefined;

      identifyUser({
        user_id: tp.sub ?? "unknown",
        access_type: accessType,
        user_type: userType,
        subscription_status: subscription?.status,
        subscription_plan: subscription?.plan_id,
        subscription_age_days: subscriptionAgeDays,
        trial_days_remaining: trialDaysRemaining,
        squawk_channels: appRoles
          .filter((r) => r.startsWith("squawk-"))
          .sort()
          .join(","),
        has_app_pro: appRoles.includes("app-pro"),
        has_equities_suite: appRoles.includes("equities-suite-widgets"),
        email_sha256: emailSha256,
      });
    })();
  }, [
    authenticated,
    tokenParsed,
    keycloakInstance,
    subscription,
    isSubscriptionLoading,
  ]);
};
